import { useEffect } from 'react';
import type { Cotizacion, LineaDesglose, OpcionTarifa } from '../../types/suscripciones';
import { useRole } from '../../hooks/useRole';

const euros = (n: number) => `${n.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

/**
 * "Suelo · 2 clases/sem" o, en una combinación, "Híbrido fundador · 1 Suelo +
 * 2 Máquina fundador".
 */
export const textoLineaDesglose = (l: LineaDesglose) => (l.componentes?.length
    ? `${l.nombre} · ${l.componentes.map((c) => `${c.clases} ${c.nombre}`).join(' + ')}`
    : `${l.nombre} · ${l.clases} clase${l.clases !== 1 ? 's' : ''}/sem`);

/** Líneas del precio: "Suelo · 1 clase/sem · 45 €". */
export const DesglosePrecio = ({ desglose, total }: { desglose?: LineaDesglose[]; total?: number }) => {
    if (!desglose?.length) return null;
    return (
        <div className="text-sm">
            <ul className="space-y-0.5">
                {desglose.map((l, i) => (
                    <li key={`${l.combinacionId ?? l.tarifaId ?? 'general'}-${i}`} className="flex justify-between gap-4">
                        <span className="text-gray-600">{textoLineaDesglose(l)}</span>
                        <span className="text-gray-800 whitespace-nowrap shrink-0">{euros(l.importe)}</span>
                    </li>
                ))}
            </ul>
            {total !== undefined && desglose.length > 1 && (
                <div className="flex justify-between gap-4 border-t mt-1 pt-1 font-semibold">
                    <span>Total</span>
                    <span>{euros(total)}</span>
                </div>
            )}
        </div>
    );
};

/**
 * Pide la tarifa de los niveles que tienen más de una (p. ej. "Máquina
 * fundador" o "Máquina general"). Las opciones vienen de la cotización
 * (`elecciones`), que las devuelve se haya elegido o no: así la elección se
 * puede cambiar siempre. Antes se recordaban aquí dentro y se perdían si el
 * selector se ocultaba un momento (al marcar y desmarcar precio fijo).
 *
 * `elegidas` es { nivelId: tarifaId }; el padre manda `Object.values(elegidas)`
 * como `tarifasAsignadas` al cotizar y al guardar.
 *
 * Tarifas cerradas a nuevas altas: un operador no las ve (salvo la que ya
 * esté elegida) y, si solo le queda una opción, se elige sola. Un admin sí las
 * ve, marcadas, y elegirlas pide confirmación por si es un error.
 */
export const SelectorTarifas = ({
    cotizacion,
    nivelesActuales,
    elegidas,
    onChange,
    nombreNivel,
    soloPendientes = false,
}: {
    cotizacion?: Cotizacion;
    nivelesActuales: string[];
    elegidas: Record<string, string>;
    onChange: (nivelId: string, tarifaId: string) => void;
    nombreNivel: (nivelId: string) => string | undefined;
    // Al añadir/mover una plaza de un cliente que ya existe: solo pedir la
    // tarifa del nivel que aún no tiene (o la que se acaba de elegir aquí).
    // Cambiar una tarifa que ya tiene se hace en Editar suscripción.
    soloPendientes?: boolean;
}) => {
    const opcionesPorNivel: Record<string, OpcionTarifa[]> = Object.fromEntries(
        [...(cotizacion?.elecciones || []), ...(cotizacion?.faltaElegir || [])].map((e) => [e.nivelId, e.opciones])
    );
    const pendientes = new Set([
        ...(cotizacion?.faltaElegir || []).map((f) => f.nivelId),
        ...Object.keys(elegidas).filter((n) => elegidas[n]),
    ]);
    const niveles = Object.keys(opcionesPorNivel)
        .filter((n) => nivelesActuales.includes(n))
        .filter((n) => !soloPendientes || pendientes.has(n));

    const { isAdmin, isSuperAdmin } = useRole();
    const esAdmin = isAdmin || isSuperAdmin;
    const visibles = (nivelId: string) => opcionesPorNivel[nivelId]
        .filter((o) => !o.cerrada || esAdmin || elegidas[nivelId] === o._id);

    // Si a un operador solo le queda una tarifa abierta, no hace falta preguntar.
    const unicasSinElegir = niveles
        .filter((n) => !elegidas[n] && visibles(n).length === 1)
        .map((n) => [n, visibles(n)[0]._id] as const);
    const claveUnicas = JSON.stringify(unicasSinElegir);
    useEffect(() => {
        unicasSinElegir.forEach(([nivelId, tarifaId]) => onChange(nivelId, tarifaId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claveUnicas]);

    const elegir = (nivelId: string, tarifaId: string) => {
        const opcion = opcionesPorNivel[nivelId].find((o) => o._id === tarifaId);
        if (opcion?.cerrada && elegidas[nivelId] !== tarifaId
            && !window.confirm(`"${opcion.nombre}" está cerrada a nuevas altas. ¿Asignarla a este cliente de todos modos?`)) {
            return;
        }
        onChange(nivelId, tarifaId);
    };

    if (!niveles.length) return null;

    return (
        <div className="space-y-2">
            {niveles.map((nivelId) => (
                <div key={nivelId}>
                    <label className="block text-sm font-medium text-gray-700">
                        Tarifa de {nombreNivel(nivelId) || 'este nivel'} *
                    </label>
                    <select
                        value={elegidas[nivelId] || ''}
                        onChange={(e) => elegir(nivelId, e.target.value)}
                        className={`mt-1 block w-full rounded-md shadow-sm ${elegidas[nivelId] ? 'border-gray-300' : 'border-amber-400'}`}
                    >
                        <option value="">Elige la tarifa…</option>
                        {visibles(nivelId).map((o) => (
                            <option key={o._id} value={o._id}>{o.nombre}{o.cerrada ? ' (cerrada a nuevas altas)' : ''}</option>
                        ))}
                    </select>
                </div>
            ))}
        </div>
    );
};

/** Mensaje de por qué no hay precio (falta elegir tarifa o falta un precio). */
export const AvisoPrecio = ({ cotizacion }: { cotizacion?: Cotizacion }) => {
    if (!cotizacion || cotizacion.ok || cotizacion.sinPlazas) return null;
    if (cotizacion.faltaElegir.length) {
        return <p className="text-sm text-amber-700">Elige la tarifa para calcular el importe.</p>;
    }
    return <p className="text-sm text-red-600">{cotizacion.errores.join(' ')}</p>;
};

/** Error de la API de precio (400 TARIFA_REQUERIDA / SIN_PRECIO / TARIFA_CERRADA), si lo es. */
export const esErrorDePrecio = (error: any): string | null => {
    const data = error?.response?.data;
    if (error?.response?.status === 400 && ['TARIFA_REQUERIDA', 'SIN_PRECIO', 'TARIFA_CERRADA'].includes(data?.code)) {
        return data.message;
    }
    return null;
};
